import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { threadService } from '@/services/threads';
import type { ThreadState } from '@/types/threads';

const initialState: ThreadState = {
  threads: [],
  isLoading: false,
  error: null,
  activeThreadId: null,
};

export const actionListThreads = createAsyncThunk('app/listThreads', async () => {
    const response = await threadService.listThreads();
    return response;
  });

export const actionCreateThread = createAsyncThunk(
  'thread/createThread',
  async ({ personaId, userPrompt }: { personaId: string; userPrompt: string }) => {
    const response = await threadService.createThread({ personaId, userPrompt });
    return { ...response, personaId };
  }
);

export const actionGetThreadInfo = createAsyncThunk(
  'thread/getThreadInfo',
  async (threadId: string) => {
    const response = await threadService.getThreadInfo(threadId);
    return response;
  }
);

const threadSlice = createSlice({
  name: 'thread',
  initialState,
  reducers: {
    resetThreads: (state) => {
      state.threads = [];
      state.isLoading = false;
      state.error = null;
      state.activeThreadId = null;
    },
    setActiveThread: (state, action) => {
      state.activeThreadId = action.payload;
    },
    clearActiveThread: (state) => {
      state.activeThreadId = null;
    },
    addThread: (state, action) => {
      const { threadID, threadTitle, personaID } = action.payload;
      // Add the thread if it doesn't exist yet
      const existingThread = state.threads.find(thread => thread.threadID === threadID);
      if (!existingThread) {
        state.threads.push({
          threadID,
          threadTitle,
          personaID,
        });
      }
      // Set as active thread
      state.activeThreadId = threadID;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(actionListThreads.fulfilled, (state, action) => {
      state.threads = action.payload.threads;
      state.isLoading = false;
    });
    builder.addCase(actionListThreads.rejected, (state, action) => {
      state.error = action.error.message || null;
      state.isLoading = false;
    });
    builder.addCase(actionListThreads.pending, (state) => {
      state.isLoading = true;
    });
    
    // Thread creation handling
    builder.addCase(actionCreateThread.fulfilled, (state, action) => {
      // Add the new thread to the list if it doesn't exist yet
      const existingThread = state.threads.find(thread => thread.threadID === action.payload.threadId);
      if (!existingThread) {
        state.threads.push({
          threadID: action.payload.threadId,
          threadTitle: action.payload.title || '',
          personaID: action.payload.personaId,
        });
      }
      
      // Set the new thread as active (it will be the last in the list)
      state.activeThreadId = action.payload.threadId;
      state.isLoading = false;
      state.error = null;
    });
    builder.addCase(actionCreateThread.rejected, (state, action) => {
      state.error = action.error.message || null;
      state.isLoading = false;
    });
    builder.addCase(actionCreateThread.pending, (state) => {
      state.isLoading = true;
    });
    
    // Thread info retrieval handling
    builder.addCase(actionGetThreadInfo.fulfilled, (state, action) => {
      state.activeThreadId = action.payload.threadID;
      state.isLoading = false;
      state.error = null;
    });
    builder.addCase(actionGetThreadInfo.rejected, (state, action) => {
      state.error = action.error.message || null;
      state.isLoading = false;
    });
    builder.addCase(actionGetThreadInfo.pending, (state) => {
      state.isLoading = true;
    });
  },    
});

export const { resetThreads, setActiveThread, clearActiveThread, addThread } = threadSlice.actions;
export default threadSlice.reducer;